import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  InputAdornment,
  Alert,
  Slider,
  Typography,
} from '@mui/material';
import { WorkspacePremiumRounded } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { scholarshipApi } from '../../../lib/api/scholarship.api';
import { TenantDateTimeField } from '../../../components/TenantDateTimeField';
import { errorText } from '../../../lib/error-text';

// Workaround for MUI's exactOptionalPropertyTypes incompatibility in Autocomplete renderInput.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const autocompleteField = (params: any) => params as React.ComponentProps<typeof TextField>;

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  termId: string;
  termName: string;
}

interface FormState {
  name: string;
  description: string;
  coveragePercent: number;
  maxApplicants: string;
  applicationDeadline: string;
  eligibleProgramIds: string[];
}

const DEFAULT_FORM: FormState = {
  name: '',
  description: '',
  coveragePercent: 50,
  maxApplicants: '',
  applicationDeadline: '',
  eligibleProgramIds: [],
};

export function CreateScholarshipDialog({ open, onClose, tenantId, termId, termName }: Props) {
  const { t } = useTranslation('sis');
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [programs, setPrograms] = useState<Array<{ id: string; name: string }>>([]);

  // Programs for the eligibility selector (empty selection = open to all).
  useEffect(() => {
    if (!tenantId || !open) return;
    const unsub = onSnapshot(collection(db, `tenants/${tenantId}/programs`), (snap) => {
      setPrograms(
        snap.docs.map((d) => ({ id: d.id, name: (d.data()['name'] as string | undefined) ?? '—' })),
      );
    });
    return () => unsub();
  }, [tenantId, open]);

  const { mutate, isPending, error, reset } = useMutation({
    mutationFn: () => {
      const params: Parameters<typeof scholarshipApi.createScholarship>[0] = {
        tenantId,
        termId,
        name: form.name,
        description: form.description,
        coveragePercent: form.coveragePercent,
        applicationDeadline: form.applicationDeadline || null,
      };
      if (form.maxApplicants) {
        params.maxApplicants = parseInt(form.maxApplicants, 10);
      }
      if (form.eligibleProgramIds.length > 0) {
        params.eligibleProgramIds = form.eligibleProgramIds;
      }
      return scholarshipApi.createScholarship(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scholarships', termId] });
      handleClose();
    },
  });

  const handleClose = () => {
    setForm(DEFAULT_FORM);
    reset();
    onClose();
  };

  const isValid = form.name.trim().length > 0 && form.description.trim().length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WorkspacePremiumRounded color="primary" />
          <span>Crear Beca para {termName}</span>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} pt={1}>
          {error && <Alert severity="error">{errorText(error, 'Error al crear la beca')}</Alert>}

          <TextField
            label="Nombre de la beca"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            required
            placeholder="Ej: Beca de Excelencia Académica"
          />

          <TextField
            label="Descripción"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            required
            multiline
            rows={3}
            placeholder="Describe los requisitos y el propósito de la beca..."
          />

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Porcentaje de cobertura: <strong>{form.coveragePercent}%</strong>
            </Typography>
            <Slider
              value={form.coveragePercent}
              onChange={(_, v) => setForm((f) => ({ ...f, coveragePercent: v as number }))}
              min={10}
              max={100}
              step={5}
              marks={[
                { value: 25, label: '25%' },
                { value: 50, label: '50%' },
                { value: 75, label: '75%' },
                { value: 100, label: '100%' },
              ]}
              valueLabelDisplay="auto"
              sx={{ color: 'primary.main' }}
            />
          </Stack>

          <TextField
            label="Máximo de postulantes"
            value={form.maxApplicants}
            onChange={(e) => setForm((f) => ({ ...f, maxApplicants: e.target.value }))}
            type="number"
            inputProps={{ min: 1 }}
            fullWidth
            helperText="Deja vacío para aceptar postulantes ilimitados"
            InputProps={{
              startAdornment: <InputAdornment position="start">#</InputAdornment>,
            }}
          />

          <TenantDateTimeField
            label="Fecha límite de postulación"
            value={form.applicationDeadline}
            onChange={(v) => setForm((f) => ({ ...f, applicationDeadline: v }))}
            fullWidth
            helperText="Deja vacío para no establecer fecha límite"
          />

          <Autocomplete
            multiple
            options={programs}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={programs.filter((p) => form.eligibleProgramIds.includes(p.id))}
            onChange={(_, selected) =>
              setForm((f) => ({ ...f, eligibleProgramIds: selected.map((s) => s.id) }))
            }
            renderInput={(params) => (
              <TextField
                {...autocompleteField(params)}
                label={t('scholarshipsPage.form.eligiblePrograms')}
                helperText={t('scholarshipsPage.form.eligibleProgramsHint')}
              />
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isPending}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => mutate()}
          disabled={isPending || !isValid}
          sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
        >
          {isPending ? 'Creando...' : 'Crear Beca'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
